import { useState, useEffect, useRef } from 'react';
import pLimit from 'p-limit';
import { ITemporaryUploadedFile } from 'views/ProFormaUpload/types';
import useS3Upload from './useS3Upload';
import getUploadUrls from './useUploadUrls';

interface UseAwsUploadProps {
    organizationCorrelationId: string;
    feature: string
    onError?: (message: string) => void;
}

// Descriptive error handling messages, this will tell what step hook failed
const ERROR_ACTION_CALL_MESSAGE = 'Please try again or contact customer support if the problem persists';
const ERROR_FAILED_PRESIGNED_URL = `Failed to obtain AWS presigned upload URL. ${ERROR_ACTION_CALL_MESSAGE}`;
const ERROR_FAILED_FILE_UPLOAD = `Failed to upload to AWS presigned upload URL. ${ERROR_ACTION_CALL_MESSAGE}`;

const CONCURRENT_UPLOAD_LIMIT = 3;

const useAwsUploadFiles = ({
    organizationCorrelationId, feature, onError,
}: UseAwsUploadProps) => {
    const [selectedHTML5FileToUpload, setSelectedHTML5FileToUpload] = useState<{ id: string, file: File, relativePath?: string }[]>([]);

    const [isReadyToUpload, setIsReadyToUpload] = useState(false);
    const filesToUploadRef = useRef<{ id: string, file: File }[]>([]);
    const [awsUploadedFiles, setAwsUploadedFiles] = useState<ITemporaryUploadedFile[]>([]);

    const { mutateAsync: uploadToS3Bucket } = useS3Upload({
        onError: (error: any) => {
            onError?.(ERROR_FAILED_FILE_UPLOAD);
        },
        onSuccess: (res: any, variables) => {
            const { id, file } = variables;
            const url = res.config.url!;
            const bucketName = url.split('//')[1].split('.')[0];
            const objectKey = res.config.data.get('key');

            setAwsUploadedFiles(prev => [
                ...prev,
                {
                    id,
                    name: file.name,
                    url,
                    objectKey,
                    bucketName,
                },
            ]);
        },
    });

    const uploadFilesWithLimit = async (files: { id: string, file: File }[]) => {
        const limit = pLimit(CONCURRENT_UPLOAD_LIMIT);

        await Promise.all(
            files.map(({ id, file }) => limit(async () => {
                const params = {
                    file_name: file.name,
                    feature,
                    public_read: false,
                    organization_correlation_id: organizationCorrelationId,
                };
                try {
                    const res = await getUploadUrls({ queryKey: ['UploadUrl', params] });
                    await uploadToS3Bucket({ urlData: res.url, file, id });
                } catch (err) {
                    onError?.(ERROR_FAILED_PRESIGNED_URL);
                }
            })),
        );
    };

    const handleUpload = async () => {
        const files = selectedHTML5FileToUpload.map(item => ({
            id: item.id,
            file: item.file,
        }));
        filesToUploadRef.current = files;
        setIsReadyToUpload(true);
        await uploadFilesWithLimit(files);
    };

    const clearAttachmentsStates = () => {
        setSelectedHTML5FileToUpload([]);
        filesToUploadRef.current = [];
        setIsReadyToUpload(false);
    };

    useEffect(() => () => {
        clearAttachmentsStates();
    }, []);

    return {
        selectedHTML5FileToUpload,
        setSelectedHTML5FileToUpload,
        filesToUploadRef,
        isReadyToUpload,
        setIsReadyToUpload,
        clearAttachmentsStates,
        handleUpload,
        awsUploadedFiles,
        setAwsUploadedFiles,
        uploadFilesWithLimit,
    };
};

export default useAwsUploadFiles;
